import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { HeartIcon, LoaderIcon } from '../../../../components/Icons';
import styles from './ChapterAINotes.module.css';

const ChapterAINotes = ({
  chapter,
  onWordClick,
  onSaveNote,
  savedNotes = {},
  savedSentenceSet,
  savingNotes = {},
  sentenceTimings = { en: {}, cn: {} },
  onSentencePronounce,
}) => {
  const [aiNotes, setAiNotes] = useState([]);
  const [notesError, setNotesError] = useState('');
  const [notesMessage, setNotesMessage] = useState('');

  const aiNotesSource = chapter?.ai_notes ?? chapter?.aiNotes ?? null;

  useEffect(() => {
    setAiNotes([]);
    setNotesError('');
    setNotesMessage('');

    if (!chapter?.id) {
      return;
    }

    if (
      aiNotesSource === null ||
      aiNotesSource === undefined ||
      (typeof aiNotesSource === 'string' && aiNotesSource.trim().length === 0)
    ) {
      setNotesMessage('当前章节尚未生成 AI 笔记');
      return;
    }

    let parsed;
    try {
      if (Array.isArray(aiNotesSource)) {
        parsed = aiNotesSource;
      } else if (typeof aiNotesSource === 'string') {
        parsed = JSON.parse(aiNotesSource);
      } else if (typeof aiNotesSource === 'object') {
        parsed = Array.isArray(aiNotesSource.notes) ? aiNotesSource.notes : aiNotesSource;
      } else {
        parsed = [];
      }
    } catch (error) {
      console.error('解析 AI 笔记失败:', error);
      setNotesError('AI 笔记数据解析失败');
      return;
    }

    const notesArray = Array.isArray(parsed) ? parsed : [];

    const normalized = notesArray
      .map((item, idx) => {
        if (!item || typeof item !== 'object') {
          return null;
        }

        const candidateId =
          item.sentence_id ??
          item.sentenceId ??
          item.id ??
          item.index ??
          idx + 1;
        const numericId = Number(candidateId);
        const sentenceId = Number.isFinite(numericId) ? numericId : candidateId;

        const keyPhrasesSource = Array.isArray(item.key_phrases)
          ? item.key_phrases
          : Array.isArray(item.keyPhrases)
            ? item.keyPhrases
            : [];

        const keyPhrases = keyPhrasesSource
          .map((phrase) => {
            if (!phrase || typeof phrase !== 'object') {
              return null;
            }

            const exampleEn =
              phrase.example_en ??
              phrase.exampleEn ??
              phrase.example ??
              '';
            const exampleCn =
              phrase.example_cn ??
              phrase.exampleCn ??
              phrase.example_translation ??
              phrase.exampleTranslation ??
              '';
            const usageNote =
              phrase.usage_note ??
              phrase.usageNote ??
              phrase.note ??
              '';
            const phraseText = phrase.phrase ?? phrase.text ?? '';
            const meaning = phrase.meaning ?? phrase.translation ?? '';

            if (
              !phraseText &&
              !meaning &&
              !usageNote &&
              !exampleEn &&
              !exampleCn
            ) {
              return null;
            }

            return {
              phrase: phraseText,
              meaning,
              usage_note: usageNote,
              example_en: exampleEn,
              example_cn: exampleCn,
            };
          })
          .filter(Boolean);

        return {
          ...item,
          sentence_id: sentenceId,
          key_phrases: keyPhrases,
        };
      })
      .filter(Boolean);

    if (normalized.length === 0) {
      setNotesMessage('当前章节尚未生成 AI 笔记');
      return;
    }

    normalized.sort((a, b) => {
      const left = Number(a.sentence_id);
      const right = Number(b.sentence_id);
      if (Number.isFinite(left) && Number.isFinite(right)) {
        return left - right;
      }
      return 0;
    });

    setAiNotes(normalized);
  }, [chapter?.id, aiNotesSource]);

  useEffect(() => {
    if (savedSentenceSet instanceof Set) {
      console.log('[AI Notes] saved sentences set', Array.from(savedSentenceSet));
    } else {
      console.log('[AI Notes] saved sentences set unavailable', savedSentenceSet);
    }
  }, [savedSentenceSet]);

  const sentencePairs = useMemo(() => {
    const results = [];

    const parseSource = (rawSource) => {
      const entries = [];
      if (!rawSource) {
        return entries;
      }

      const pushEntry = (text, meta) => {
        if (text === undefined || text === null) {
          return;
        }
        const value = text.toString().trim();
        if (!value) {
          return;
        }

        const metaObj = typeof meta === 'object' && meta !== null ? meta : {};
        const fallbackId = entries.length + 1;
        const candidateId =
          metaObj.sentence_id ??
          metaObj.sentenceId ??
          metaObj.id ??
          metaObj.index ??
          metaObj.order_index ??
          metaObj.sentence_index ??
          fallbackId;
        const numericId = Number(candidateId);
        const sentenceId = Number.isFinite(numericId) ? numericId : candidateId;

        entries.push({
          text: value,
          sentenceId,
        });
      };

      const processItem = (item, index) => {
        if (typeof item === 'string') {
          pushEntry(item, { sentence_id: index + 1 });
          return;
        }

        if (!item || typeof item !== 'object') {
          return;
        }

        const text =
          item.sentence_content ??
          item.content ??
          item.text ??
          item.original ??
          item.en ??
          item.cn ??
          '';

        pushEntry(text, item);
      };

      const tryParseJson = (value) => {
        if (typeof value !== 'string') {
          return value;
        }
        try {
          return JSON.parse(value);
        } catch (error) {
          return null;
        }
      };

      const parsed = tryParseJson(rawSource);
      if (parsed) {
        if (Array.isArray(parsed)) {
          parsed.forEach((item, idx) => processItem(item, idx));
        } else if (typeof parsed === 'object') {
          if (Array.isArray(parsed.sentences)) {
            parsed.sentences.forEach((item, idx) => processItem(item, idx));
          } else if (Array.isArray(parsed.paragraphs)) {
            parsed.paragraphs.forEach((paragraph) => {
              if (Array.isArray(paragraph?.sentences)) {
                paragraph.sentences.forEach((item, idx) => processItem(item, idx));
              } else {
                processItem(paragraph, entries.length);
              }
            });
          } else {
            processItem(parsed, 0);
          }
        }

        if (entries.length > 0) {
          return entries;
        }
      }

      if (typeof rawSource === 'string') {
        rawSource.split(/\n+/).forEach((line, idx) => {
          pushEntry(line, { sentence_id: idx + 1 });
        });
      }

      return entries;
    };

    const originalEntries = parseSource(chapter?.content);
    const translatedEntries = parseSource(chapter?.content_trans);
    const maxLength = Math.max(originalEntries.length, translatedEntries.length);

    for (let index = 0; index < maxLength; index += 1) {
      const original = originalEntries[index] || {};
      const translated = translatedEntries[index] || {};

      if (!original.text && !translated.text) {
        continue;
      }

      const sentenceId =
        translated.sentenceId ??
        original.sentenceId ??
        index + 1;

      results.push({
        sentenceId,
        index,
        english: translated.text || '',
        chinese: original.text || '',
      });
    }

    return results;
  }, [chapter?.content, chapter?.content_trans]);

  const sentenceLookup = useMemo(() => {
    const map = new Map();
    sentencePairs.forEach((pair, idx) => {
      const { sentenceId } = pair;
      const numericId = Number(sentenceId);

      if (sentenceId !== undefined && sentenceId !== null) {
        const key = String(sentenceId);
        if (!map.has(key)) {
          map.set(key, pair);
        }
      }

      if (Number.isFinite(numericId)) {
        if (!map.has(numericId)) {
          map.set(numericId, pair);
        }
        const numericMinusOne = numericId - 1;
        if (!map.has(numericMinusOne)) {
          map.set(numericMinusOne, pair);
        }
      }

      if (!map.has(idx)) {
        map.set(idx, pair);
      }
      const idxPlusOne = idx + 1;
      if (!map.has(idxPlusOne)) {
        map.set(idxPlusOne, pair);
      }
    });
    return map;
  }, [sentencePairs]);

  const resolveSentenceContent = useCallback(
    (note, index) => {
      if (!note) {
        return {
          sentenceId: index + 1,
          english: '',
          chinese: '',
        };
      }

      const fallbackSentenceId = note.sentence_id ?? note.sentenceId ?? index + 1;
      const fallbackEnglish = note.sentence_en ?? '';
      const fallbackChinese = note.sentence_zh ?? '';

      const candidates = [];
      const rawId = note.sentence_id ?? note.sentenceId;
      if (rawId !== undefined && rawId !== null) {
        const numericId = Number(rawId);
        if (Number.isFinite(numericId)) {
          candidates.push(numericId);
          candidates.push(numericId - 1);
        }
        candidates.push(String(rawId));
      }
      candidates.push(index);
      candidates.push(index + 1);

      for (const key of candidates) {
        if (sentenceLookup.has(key)) {
          const pair = sentenceLookup.get(key);
          if (pair) {
            return {
              sentenceId: pair.sentenceId ?? fallbackSentenceId,
              english: pair.english || fallbackEnglish,
              chinese: pair.chinese || fallbackChinese,
            };
          }
        }
      }

      return {
        sentenceId: fallbackSentenceId,
        english: fallbackEnglish,
        chinese: fallbackChinese,
      };
    },
    [sentenceLookup]
  );

  const renderEnglishText = useCallback(
    (text, sentenceId, keyPrefix) => {
      if (!text || typeof text !== 'string') {
        return text;
      }

      const invokeWordClick = typeof onWordClick === 'function' ? onWordClick : null;
      const parts = text.split(/(\b[A-Za-z][A-Za-z'’\-]*\b)/);

      return parts.map((part, index) => {
        if (part && /^[A-Za-z][A-Za-z'’\-]*$/.test(part)) {
          if (!invokeWordClick) {
            return <Fragment key={`${keyPrefix}-${index}`}>{part}</Fragment>;
          }

          const handleSelect = () => invokeWordClick(part, sentenceId);

          return (
            <span
              key={`${keyPrefix}-${index}`}
              className={styles.clickableWord}
              role="button"
              tabIndex={0}
              onClick={handleSelect}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  handleSelect();
                }
              }}
            >
              {part}
            </span>
          );
        }

        return <Fragment key={`${keyPrefix}-${index}`}>{part}</Fragment>;
      });
    },
    [onWordClick]
  );

  return (
    <div className={styles.aiNotesWrapper}>
      {notesError && (
        <div className={styles.notesStatus} role="alert">
          <span>{notesError}</span>
        </div>
      )}
      {!notesError && notesMessage && (
        <div className={styles.notesStatus}>
          <span>{notesMessage}</span>
        </div>
      )}
      {!notesError && !notesMessage && aiNotes.length > 0 && (
        <div className={styles.aiNotesContent}>
          {aiNotes.map((item, idx) => {
            const { sentenceId, english, chinese } = resolveSentenceContent(item, idx);
            const keyPhrases = Array.isArray(item.key_phrases) ? item.key_phrases : [];
            const rawSentenceId = sentenceId ?? idx + 1;
            const numericCandidate = Number(rawSentenceId);
            const displayId =
              typeof rawSentenceId === 'string' && rawSentenceId.trim().length === 0
                ? idx + 1
                : Number.isFinite(numericCandidate)
                  ? numericCandidate
                  : rawSentenceId;
            const englishText = english ? english.toString().trim() : '';
            const chineseText = chinese ? chinese.toString().trim() : '';

            const sentenceKey = displayId != null ? String(displayId) : `note-${idx}`;
            let savedNoteId = null;
            if (savedNotes && typeof savedNotes === 'object') {
              if (Object.prototype.hasOwnProperty.call(savedNotes, sentenceKey)) {
                savedNoteId = savedNotes[sentenceKey];
              } else if (
                displayId != null &&
                Object.prototype.hasOwnProperty.call(savedNotes, String(displayId))
              ) {
                savedNoteId = savedNotes[String(displayId)];
              } else if (
                typeof displayId === 'number' &&
                Object.prototype.hasOwnProperty.call(savedNotes, displayId)
              ) {
                savedNoteId = savedNotes[displayId];
              }
            }

            const numericDisplayId = Number(displayId);
            const isSavedViaSet =
              savedSentenceSet instanceof Set &&
              Number.isFinite(numericDisplayId) &&
              savedSentenceSet.has(numericDisplayId);
            const isSaved = isSavedViaSet || Boolean(savedNoteId);
            const isSaving = Boolean(
              savingNotes[sentenceKey] ?? savingNotes[displayId]
            );

            const handleSaveNote = () => {
              if (typeof onSaveNote === 'function') {
                onSaveNote(displayId, { noteId: savedNoteId });
              }
            };

            const allowPronounce = typeof onSentencePronounce === 'function';
            const enTiming = sentenceTimings?.en?.[displayId] ?? null;
            const hasPronounce = allowPronounce && enTiming && enTiming.start != null;

            const handlePronounceClick = () => {
              if (!hasPronounce) return;
              onSentencePronounce(displayId, {
                timings: { en: enTiming },
                originalText: englishText,
                translatedText: chineseText,
              });
            };

            return (
              <div key={`note-${displayId}-${idx}`} className={styles.aiNoteItem}>
                <div className={styles.aiNoteSentenceHeader}>
                  <div className={styles.aiNoteHeaderLeft}>
                    <span className={styles.aiNoteSentenceIndex}>#{displayId}</span>
                    {allowPronounce && (
                      <button
                        type="button"
                        className={styles.pronounceBtn}
                        onClick={handlePronounceClick}
                        disabled={!hasPronounce}
                        title="播放英文发音"
                        aria-label="播放英文发音"
                      >
                        <span className="material-symbols-rounded">volume_up</span>
                      </button>
                    )}
                  </div>
                  {typeof onSaveNote === 'function' && (
                    <button
                      type="button"
                      className={`${styles.favoriteButton} ${
                        isSaved ? styles.favoriteButtonActive : ''
                      }`}
                      onClick={handleSaveNote}
                      disabled={isSaving}
                      title={isSaved ? '移除 AI 笔记' : '收藏 AI 笔记'}
                    >
                      {isSaving ? (
                        <LoaderIcon size={16} />
                      ) : (
                        <>
                          <HeartIcon size={16} filled={isSaved} />
                          <span className={styles.favoriteButtonText}>
                            {isSaved ? '已收藏' : '收藏'}
                          </span>
                        </>
                      )}
                    </button>
                  )}
                </div>
                {(englishText || chineseText) && (
                  <div className={styles.aiNoteSentences}>
                    {englishText && (
                      <div className={styles.aiNoteSentenceEn}>
                        {renderEnglishText(englishText, displayId, `sentence-${displayId}-${idx}`)}
                      </div>
                    )}
                    {chineseText && (
                      <div className={styles.aiNoteSentenceZh}>{chineseText}</div>
                    )}
                  </div>
                )}
                {keyPhrases.length > 0 && (
                  <div className={styles.aiNotePhrases}>
                    <h4 className={styles.aiNotePhrasesTitle}>
                      <span className="material-icons-round" style={{ fontSize: '14px' }}>auto_awesome</span>
                      重点短语
                    </h4>
                    {keyPhrases.map((phrase, phraseIdx) => (
                      <div key={`phrase-${displayId}-${phraseIdx}`} className={styles.aiNotePhraseItem}>
                        <div className={styles.aiNotePhraseHeader}>
                          <span className={styles.aiNotePhraseTitle}>
                            {phrase.phrase
                              ? renderEnglishText(
                                  phrase.phrase,
                                  displayId,
                                  `phrase-title-${displayId}-${phraseIdx}`,
                                )
                              : '短语'}
                          </span>
                          {phrase.meaning && (
                            <span className={styles.aiNotePhraseMeaning}>
                              {phrase.meaning}
                            </span>
                          )}
                        </div>
                        {phrase.usage_note && (
                          <p className={styles.aiNotePhraseUsage}>
                            <span className={styles.aiNoteMetaLabel}>用法</span>
                            {phrase.usage_note}
                          </p>
                        )}
                        {(phrase.example_en || phrase.example_cn) && (
                          <div className={styles.aiNotePhraseExampleBox}>
                            {phrase.example_en && (
                              <p className={styles.aiNotePhraseExample}>
                                {renderEnglishText(
                                  phrase.example_en,
                                  displayId,
                                  `example-${displayId}-${phraseIdx}`,
                                )}
                              </p>
                            )}
                            {phrase.example_cn && (
                              <p className={styles.aiNotePhraseExampleCn}>{phrase.example_cn}</p>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ChapterAINotes;
